import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { Box, Button, CircularProgress } from "@mui/material"
import React, { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import ConfirmationDialog from "../components/ConfirmationDialog"
import { useJobContext } from "../contexts/JobContext"
import {
    clearSessionStorage,
    getPreviousJobStatus,
    revertJobsQuantity,
    useBulkUpdateStatusHook,
    useUpdateStatusHook,
} from "../entities/job/helper"
import { EJobStatus, IJob, IShiftStopIds, IStop } from "../entities/job/interface"
import { getPreviousJobPart } from "../utils/job"
import ShiftContext from "../contexts/ShiftContext"

interface IJobBackNavigation {
    jobs: IJob[]
    stops: IShiftStopIds | null
    setJobStatus: (status: EJobStatus) => void // Function to update the job status in the state/context
}

const JobBackNavigation: React.FC<IJobBackNavigation> = ({ jobs, stops, setJobStatus }) => {
    const { t } = useTranslation()
    const shiftContext = useContext(ShiftContext)
    const {
        setIndexes,
        currentJobIndex,
        currentDestinationIndex,
        setIsConsecutive,
        setIsRoadMap,
        isRoadMap,
        setCurrentJobStatus,
    } = useJobContext()
    const navigate = useNavigate()
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const { handleBulkJobsUpdate, loading } = useBulkUpdateStatusHook()
    const { handleJobUpdate, jobUpdateMutationLoading } = useUpdateStatusHook()

    const handleBack = async () => {
        window.sessionStorage.setItem("screens", JSON.stringify({ consecutive: "false", roadMap: "false" }))
        window.sessionStorage.setItem("screens", JSON.stringify({ consecutive: "false", roadMap: "false" }))
        setIsConsecutive(false)

        const jobStatus = jobs[0]?.jobStatus
        const preJobStatus = getPreviousJobStatus(jobStatus)
        try {
            if (preJobStatus === EJobStatus.idle || preJobStatus === EJobStatus.loaded) {
                // we need to check if the previous job is completed or not
                // false argument is because we dont need destination stops, we need only the previous part
                const prevJob = getPreviousJobPart(
                    stops,
                    setIndexes,
                    currentDestinationIndex,
                    currentJobIndex,
                    jobs[0],
                    true,
                    false,
                    isRoadMap
                )

                // if there are no any previous job, we can go back by using job statuses
                // if there is previous job, we need to confirm the user if they want to go back
                if (prevJob) {
                    setShowConfirmDialog(true)
                    return
                } else {
                    const jobIds = jobs.filter((job) => !job.isJobCompleted).map((job) => job._id)
                    if (jobIds.length === 0) return

                    // Update all jobs to the next status
                    await handleBulkJobsUpdate(jobIds, preJobStatus)

                    // Set the selected job in the context
                    setJobStatus(preJobStatus)

                    //clear the session storage and navigate to job listing
                    clearSessionStorage([
                        "destinationStops",
                        "currentDestinationIndex",
                        "currentStopIndex",
                        "groupId",
                        "completedJobGroups",
                        "screens",
                    ])
                    navigate("/job-listing")
                }
            } else {
                const jobIds = jobs.filter((job) => !job.isJobCompleted).map((job) => job._id)
                if (jobIds.length === 0) return

                if (preJobStatus === EJobStatus.atPickupLocation || preJobStatus === EJobStatus.atDeliveryLocation) {
                    const jobsItemsRevert = await revertJobsQuantity(preJobStatus, jobs, handleJobUpdate)

                    if (jobsItemsRevert && jobsItemsRevert?.length > 0) {
                        // Set the status in the context
                        shiftContext?.updateJob(jobsItemsRevert)
                    }
                }
                // Update all jobs to the next status
                await handleBulkJobsUpdate(jobIds, preJobStatus)
            }
        } catch (error) {
            console.log(error)
        }
    }

    // Handle cancel
    const handleCancel = () => {
        setShowConfirmDialog(false)
    }

    const handleConfirmUnload = async () => {
        setShowConfirmDialog(false)

        const jobStatus = jobs[0]?.jobStatus
        const preJobStatus = getPreviousJobStatus(jobStatus)

        const jobIds = jobs.filter((job) => !job.isJobCompleted).map((job) => job._id)
        if (jobIds.length === 0) return

        // Update all jobs to the next status
        await handleBulkJobsUpdate(jobIds, preJobStatus)

        // Update the current job status in context
        setCurrentJobStatus(preJobStatus)

        const prevJobPart: IStop | IStop[] | null | boolean = getPreviousJobPart(
            stops,
            setIndexes,
            currentDestinationIndex,
            currentJobIndex,
            jobs[0],
            false,
            true,
            jobs[0]?.groupId ? true : false
        )

        // if the previous job part is an array, it means we need to update the status of all the jobs in the group
        if (Array.isArray(prevJobPart)) {
            if (jobs[0]?.groupId) {
                const screens = {
                    consecutive: "false",
                    roadMap: "true",
                }
                window.sessionStorage.setItem("screens", JSON.stringify(screens))
                return setIsRoadMap(true)
            } else {
                for (const jobPart of prevJobPart) {
                    await handleBulkJobsUpdate([jobPart?.jobId], EJobStatus.loaded)
                }
            }
        }
    }

    return (
        <>
            <Box position="absolute" top={16} left={16} zIndex={10}>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleBack}
                    sx={{
                        borderRadius: "50%",
                        minWidth: "40px",
                        height: "40px",
                        padding: 0,
                    }}
                >
                    {loading || jobUpdateMutationLoading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        <ArrowBackIcon />
                    )}
                </Button>
            </Box>
            <ConfirmationDialog
                open={showConfirmDialog}
                title={t("jobs.mobile.backAction.unloadingJob.title")}
                content={t("jobs.mobile.backAction.unloadingJob.subTitle")}
                actions={{
                    confirmText: t("common.yes"),
                    cancelText: t("common.no"),
                    onConfirm: handleConfirmUnload,
                    onCancel: () => setShowConfirmDialog(false),
                }}
                onClose={handleCancel}
            />
        </>
    )
}

export default JobBackNavigation
